import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertCircle,
  Loader2,
  ShieldCheck,
  ClipboardEdit,
  Plus,
  Wrench,
  ClipboardList,
} from 'lucide-react';
import { useCrewRoster } from '@/lib/crewRoster';
import {
  VIRTUAL_JOBS,
  recomputeFromTimes,
  createManualTimeEntry,
  updateTimeEntry,
  formatUsd,
} from '@/lib/timeEntriesData';
import { toast } from 'sonner';

/**
 * Convert an ISO timestamp to the local-datetime-input value (YYYY-MM-DDTHH:mm).
 */
const isoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Build a local-formatted string (input ignores TZ, treats as local).
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Convert a local-datetime-input value to a full ISO timestamp.
 * The value from <input type="datetime-local"> is assumed local-tz.
 */
const localInputToIso = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const isoDateOnly = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Heuristic: figure out which job_id (if any) and label should be selected
 * for an existing entry. Matches by job_id first, then by job name vs the
 * provided list of active jobs, then falls back to virtual.
 */
const resolveInitialJobSelection = (entry, jobs) => {
  if (!entry) return { jobSelectValue: '', jobLabel: '' };
  const job = (entry.job || '').trim();
  if (entry.job_id) {
    const found = jobs.find((j) => j.id === entry.job_id);
    return {
      jobSelectValue: entry.job_id,
      jobLabel: found?.name || job,
    };
  }
  if (job === 'SHOP') return { jobSelectValue: '__SHOP__', jobLabel: 'SHOP' };
  if (job === 'ADMIN') return { jobSelectValue: '__ADMIN__', jobLabel: 'ADMIN' };
  // Try to match by name
  const byName = jobs.find((j) => j.name === job);
  if (byName) return { jobSelectValue: byName.id, jobLabel: byName.name };
  return { jobSelectValue: '', jobLabel: job };
};

/**
 * Edit / Create time-entry dialog.
 *
 * Props:
 *   open, onOpenChange   — controlled open state
 *   mode                 — 'create' | 'edit'
 *   entry                — required when mode === 'edit'
 *   jobs                 — active jobs available in picker
 *   onSaved              — fires after a successful save (parent should refresh)
 */
export const EditTimeEntryDialog = ({
  open,
  onOpenChange,
  mode = 'edit',
  entry = null,
  jobs = [],
  onSaved,
}) => {
  const { t } = useTranslation();
  // Live roster — pull both lists so dropdowns can show ALL crew (incl.
  // inactive — useful when editing legacy entries) AND highlight which ones
  // are currently active.
  const { roster: activeCrew, allRoster: fullCrew } = useCrewRoster();
  const isEdit = mode === 'edit' && !!entry;
  const [crewId, setCrewId] = useState('');
  const [jobSelectValue, setJobSelectValue] = useState('');
  const [date, setDate] = useState('');
  const [clockIn, setClockIn] = useState('');
  const [clockOut, setClockOut] = useState('');
  const [hoursOverride, setHoursOverride] = useState('');
  const [costOverride, setCostOverride] = useState('');
  const [overrideHours, setOverrideHours] = useState(false);
  const [overrideCost, setOverrideCost] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reset form when dialog opens or entry changes.
  useEffect(() => {
    if (!open) return;
    setError('');
    setSubmitting(false);
    if (isEdit) {
      setCrewId(entry.crew_id || '');
      const init = resolveInitialJobSelection(entry, jobs);
      setJobSelectValue(init.jobSelectValue);
      // Prefer clock-in date; fall back to date column.
      setDate(isoDateOnly(entry.clock_in_at) || entry.date || '');
      setClockIn(isoToLocalInput(entry.clock_in_at));
      setClockOut(isoToLocalInput(entry.clock_out_at));
      setHoursOverride(String(entry.hours ?? 0));
      setCostOverride(String(entry.cost ?? 0));
      setOverrideHours(false);
      setOverrideCost(false);
    } else {
      // Create mode — sensible defaults.
      const today = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const yyyy = today.getFullYear();
      const mm = pad(today.getMonth() + 1);
      const dd = pad(today.getDate());
      setCrewId('');
      setJobSelectValue('');
      setDate(`${yyyy}-${mm}-${dd}`);
      setClockIn(`${yyyy}-${mm}-${dd}T08:00`);
      setClockOut(`${yyyy}-${mm}-${dd}T16:00`);
      setHoursOverride('');
      setCostOverride('');
      setOverrideHours(false);
      setOverrideCost(false);
    }
  }, [open, isEdit, entry, jobs]);

  // Resolve job display name and ID from the select value
  const { jobIdToSave, jobNameToSave } = useMemo(() => {
    const virtual = VIRTUAL_JOBS.find((v) => v.id === jobSelectValue);
    if (virtual) return { jobIdToSave: null, jobNameToSave: virtual.label };
    const real = jobs.find((j) => j.id === jobSelectValue);
    if (real) return { jobIdToSave: real.id, jobNameToSave: real.name };
    return { jobIdToSave: null, jobNameToSave: '' };
  }, [jobSelectValue, jobs]);

  // Live recompute when clock_in/clock_out change.
  const computed = useMemo(() => {
    const inIso = localInputToIso(clockIn);
    const outIso = localInputToIso(clockOut);
    if (!inIso || !outIso) return { hours: 0, cost: 0 };
    return recomputeFromTimes(crewId, inIso, outIso, []);
  }, [crewId, clockIn, clockOut]);

  // Keep override fields synced with computed values until the user toggles override.
  useEffect(() => {
    if (!overrideHours) setHoursOverride(String(computed.hours));
  }, [computed.hours, overrideHours]);
  useEffect(() => {
    if (!overrideCost) setCostOverride(String(computed.cost));
  }, [computed.cost, overrideCost]);

  const crew = fullCrew.find((c) => c.id === crewId);

  const VirtualIcon = ({ id }) =>
    id === '__SHOP__' ? (
      <Wrench size={12} className="text-[hsl(var(--primary))]" />
    ) : id === '__ADMIN__' ? (
      <ClipboardList size={12} className="text-[hsl(var(--primary))]" />
    ) : null;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError('');

    if (!crewId) {
      setError(t('crewClock.edit.error_pick_crew'));
      return;
    }
    if (!jobSelectValue) {
      setError(t('crewClock.edit.error_pick_job'));
      return;
    }
    const inIso = localInputToIso(clockIn);
    const outIso = localInputToIso(clockOut);
    if (!inIso) {
      setError(t('crewClock.edit.error_clock_in'));
      return;
    }
    if (!outIso) {
      setError(t('crewClock.edit.error_clock_out'));
      return;
    }
    if (new Date(outIso).getTime() <= new Date(inIso).getTime()) {
      setError(t('crewClock.edit.error_out_after_in'));
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        crew_id: crewId,
        name: crew?.name || crewId,
        job: jobNameToSave,
        job_id: jobIdToSave,
        date: date || isoDateOnly(inIso),
        clock_in_at: inIso,
        clock_out_at: outIso,
      };
      if (overrideHours) payload.hours = Math.max(0, Number(hoursOverride) || 0);
      if (overrideCost) payload.cost = Math.max(0, Number(costOverride) || 0);

      let saved;
      if (isEdit) {
        saved = await updateTimeEntry(entry.id, payload);
        toast.success(t('crewClock.edit.entry_updated'));
      } else {
        saved = await createManualTimeEntry({
          crewId,
          jobId: jobIdToSave,
          jobName: jobNameToSave,
          date: payload.date,
          clockInAt: inIso,
          clockOutAt: outIso,
          hoursOverride: overrideHours ? hoursOverride : null,
          costOverride: overrideCost ? costOverride : null,
        });
        toast.success(t('crewClock.edit.entry_created'));
      }
      onSaved?.(saved);
      onOpenChange?.(false);
    } catch (err) {
      setError(err?.message || t('crewClock.edit.error_save'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--hairline))] max-w-lg max-h-[92vh] overflow-y-auto"
        data-testid="edit-time-entry-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
            <ShieldCheck size={14} />
            <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-muted-foreground">
              {t('crewClock.edit.admin')}
            </span>
            {isEdit ? (
              <Badge
                variant="outline"
                className="bg-[hsl(var(--muted))] text-muted-foreground border-border uppercase text-[10px] tracking-[0.16em]"
              >
                {t('crewClock.edit.editing')}
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30 uppercase text-[10px] tracking-[0.16em]"
              >
                {t('crewClock.edit.new')}
              </Badge>
            )}
          </div>
          <DialogTitle className="text-foreground tracking-tight inline-flex items-center gap-2">
            {isEdit ? (
              <>
                <ClipboardEdit size={16} /> {t('crewClock.edit.edit_title')}
              </>
            ) : (
              <>
                <Plus size={16} /> {t('crewClock.edit.new_title')}
              </>
            )}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {isEdit
              ? t('crewClock.edit.edit_description')
              : t('crewClock.edit.new_description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-1" noValidate>
          {/* Crew + Job */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                {t('crewClock.edit.crew_required')}
              </Label>
              <Select value={crewId} onValueChange={setCrewId}>
                <SelectTrigger
                  className="h-10 bg-[hsl(var(--muted))] border-border"
                  data-testid="edit-entry-crew"
                >
                  <SelectValue placeholder={t('crewClock.edit.select_crew')} />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-border max-h-72">
                  {/* Show ACTIVE crew first, then inactive crew (so admins can
                      reassign legacy entries to ex-employees without surprises). */}
                  {activeCrew.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                      <span className="ml-2 text-xs text-muted-foreground">
                        {t('crewClock.edit.rate_suffix', { rate: formatUsd(c.rate) })}
                      </span>
                    </SelectItem>
                  ))}
                  {fullCrew.length > activeCrew.length && (
                    <>
                      <SelectSeparator />
                      {fullCrew
                        .filter((c) => !activeCrew.some((a) => a.id === c.id))
                        .map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            <span className="text-muted-foreground">
                              {c.name}
                            </span>
                            <span className="ml-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                              {t(`settings.crew.status.${c.status || 'inactive'}`, {
                                defaultValue: 'inactive',
                              })}
                            </span>
                          </SelectItem>
                        ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                {t('crewClock.edit.job_required')}
              </Label>
              <Select value={jobSelectValue} onValueChange={setJobSelectValue}>
                <SelectTrigger
                  className="h-10 bg-[hsl(var(--muted))] border-border"
                  data-testid="edit-entry-job"
                >
                  <SelectValue placeholder={t('crewClock.edit.select_job')} />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-border max-h-72">
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      {t('crewClock.internal_time')}
                    </SelectLabel>
                    {VIRTUAL_JOBS.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        <span className="inline-flex items-center gap-2">
                          <VirtualIcon id={v.id} />
                          <span className="font-semibold">{v.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                  <SelectSeparator />
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      {t('crewClock.active_jobs')}
                    </SelectLabel>
                    {jobs.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-muted-foreground">
                        {t('crewClock.edit.no_active_jobs')}
                      </div>
                    ) : (
                      jobs.map((j) => (
                        <SelectItem key={j.id} value={j.id}>
                          {j.name} — {j.client}
                        </SelectItem>
                      ))
                    )}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Date */}
          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              {t('crewClock.edit.date')}
            </Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
              data-testid="edit-entry-date"
            />
            <div className="text-[10px] text-muted-foreground">
              {t('crewClock.edit.date_helper')}
            </div>
          </div>

          {/* Clock in / out */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                {t('crewClock.edit.clock_in_required')}
              </Label>
              <Input
                type="datetime-local"
                value={clockIn}
                onChange={(e) => setClockIn(e.target.value)}
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
                data-testid="edit-entry-clock-in"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                {t('crewClock.edit.clock_out_required')}
              </Label>
              <Input
                type="datetime-local"
                value={clockOut}
                onChange={(e) => setClockOut(e.target.value)}
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
                data-testid="edit-entry-clock-out"
              />
            </div>
          </div>

          {/* Computed hours/cost preview */}
          <div
            className="rounded-md border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 p-3 grid grid-cols-2 gap-3"
            data-testid="edit-entry-computed"
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('crewClock.hours_computed')}
              </div>
              <div className="text-base font-semibold text-foreground tabular-nums">
                {computed.hours.toFixed(2)}h
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('crewClock.cost_computed')}
              </div>
              <div className="text-base font-semibold text-[hsl(var(--success))] tabular-nums">
                {formatUsd(computed.cost)}
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {crew
                  ? `${crew.name} \u00b7 ${t('crewClock.edit.rate_suffix', {
                      rate: formatUsd(crew.rate),
                    })}`
                  : t('crewClock.edit.pick_crew_first')}
              </div>
            </div>
          </div>

          {/* Optional manual overrides */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={overrideHours}
                  onChange={(e) => setOverrideHours(e.target.checked)}
                  className="accent-[hsl(var(--primary))]"
                  data-testid="edit-entry-override-hours-toggle"
                />
                Override hours
              </label>
              <Input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={hoursOverride}
                onChange={(e) => setHoursOverride(e.target.value)}
                disabled={!overrideHours}
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground disabled:opacity-50"
                data-testid="edit-entry-hours"
              />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={overrideCost}
                  onChange={(e) => setOverrideCost(e.target.checked)}
                  className="accent-[hsl(var(--primary))]"
                  data-testid="edit-entry-override-cost-toggle"
                />
                {t('crewClock.override_cost')}
              </label>
              <Input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={costOverride}
                onChange={(e) => setCostOverride(e.target.value)}
                disabled={!overrideCost}
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground disabled:opacity-50"
                data-testid="edit-entry-cost"
              />
            </div>
          </div>

          {error ? (
            <Alert
              className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
              data-testid="edit-entry-error"
            >
              <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter className="gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange?.(false)}
              disabled={submitting}
              className="text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]"
            >
              {t('actions.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
              data-testid="edit-entry-submit"
            >
              {submitting ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
              {isEdit
                ? t('crewClock.edit.save_changes')
                : t('crewClock.edit.create_entry')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditTimeEntryDialog;
